/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  mdiAccountSettings,
  mdiAlignHorizontalDistribute,
  mdiAlignVerticalCenter,
  mdiAlphabetical,
  mdiArrowDown,
  mdiArrowExpandHorizontal,
  mdiArrowLeft,
  mdiArrowRight,
  mdiArrowRightBottom,
  mdiArrowULeftTop,
  mdiArrowUp,
  mdiBookVariant,
  mdiBookVariantMultiple,
  mdiCalendar,
  mdiCalendarClock,
  mdiCellphone,
  mdiCheck,
  mdiChevronDown,
  mdiChevronRight,
  mdiClose,
  mdiCodeArray,
  mdiCodeBracesBox,
  mdiCodeString,
  mdiCog,
  mdiContentCopy,
  mdiContentDuplicate,
  mdiContentPaste,
  mdiContentSave,
  mdiCubeOutline,
  mdiCursorDefault,
  mdiCursorMove,
  mdiDockBottom,
  mdiDockLeft,
  mdiDockRight,
  mdiDomain,
  mdiDotsHorizontal,
  mdiDotsVertical,
  mdiDrag,
  mdiFileTree,
  mdiFilter,
  mdiEmailOutline,
  mdiEyeOffOutline,
  mdiEyeOutline,
  mdiFlagVariant,
  mdiFormatAlignCenter,
  mdiFormatAlignJustify,
  mdiFormatAlignLeft,
  mdiFormatAlignRight,
  mdiFormatHorizontalAlignCenter,
  mdiFormatHorizontalAlignLeft,
  mdiFormatHorizontalAlignRight,
  mdiFormatIndentDecrease,
  mdiFormatIndentIncrease,
  mdiFormDropdown,
  mdiGroup,
  mdiHomeVariant,
  mdiIdentifier,
  mdiInformationVariant,
  mdiKeyVariant,
  mdiLaptop,
  mdiLoginVariant,
  mdiLogoutVariant,
  mdiMagnify,
  mdiMathCompass,
  mdiMenuDown,
  mdiMinus,
  mdiMonitor,
  mdiMonitorSmall,
  mdiOpenInNew,
  mdiPageNextOutline,
  mdiGestureTapHold,
  mdiPaletteOutline,
  mdiPencil,
  mdiPlus,
  mdiRedo,
  mdiShieldLock,
  mdiTablet,
  mdiThemeLightDark,
  mdiTune,
  mdiUndo,
  mdiVariable,
  mdiVectorSquare,
  mdiWeatherNight,
  mdiWeb,
  mdiWebBox,
  mdiWhiteBalanceSunny,
  mdiWindowClose,
} from '@aglyn/shared-data-mdi'

// Data-Type
export const ICON_VARIANT_ARRAY = mdiCodeArray
export const ICON_VARIANT_COLLECTION = mdiBookVariantMultiple
export const ICON_VARIANT_DATE = mdiCalendar
export const ICON_VARIANT_DATE_TIME = mdiCalendarClock
export const ICON_VARIANT_DOCUMENT = mdiBookVariant
export const ICON_VARIANT_COMPONENT = mdiCubeOutline
export const ICON_VARIANT_ELEMENT = mdiVectorSquare
export const ICON_VARIANT_IDENTIFIER = mdiIdentifier
export const ICON_VARIANT_OBJECT = mdiCodeBracesBox
export const ICON_VARIANT_PRIMARY_KEY = mdiKeyVariant
export const ICON_VARIANT_PROPERTY = mdiVariable
export const ICON_VARIANT_STRING = mdiCodeString
export const ICON_VARIANT_TEXT = mdiAlphabetical

// Flexbox
export const ICON_VARIANT_FLEX_START = mdiFormatHorizontalAlignRight
export const ICON_VARIANT_FLEX_END = mdiFormatHorizontalAlignLeft
export const ICON_VARIANT_FLEX_CENTER = mdiFormatHorizontalAlignCenter
export const ICON_VARIANT_FLEX_SPACE_EVENLY = mdiAlignHorizontalDistribute
export const ICON_VARIANT_FLEX_SPACE_AROUND = mdiAlignHorizontalDistribute
export const ICON_VARIANT_FLEX_SPACE_BETWEEN = mdiAlignVerticalCenter

// Global CSS Values
export const ICON_VARIANT_CSS_DEFAULT = mdiMinus
export const ICON_VARIANT_CSS_INHERIT = mdiArrowRightBottom
export const ICON_VARIANT_ALIGN_LEFT = mdiFormatAlignLeft
export const ICON_VARIANT_ALIGN_CENTER = mdiFormatAlignCenter
export const ICON_VARIANT_ALIGN_RIGHT = mdiFormatAlignRight
export const ICON_VARIANT_ALIGN_JUSTIFY = mdiFormatAlignJustify

// Action + Navigation
export const ICON_VARIANT_CLEAR = mdiClose
export const ICON_VARIANT_CLOSE = mdiClose
export const ICON_VARIANT_COLLAPSIBLE_CLOSE = mdiChevronDown
export const ICON_VARIANT_COLLAPSIBLE_OPEN = mdiChevronRight
export const ICON_VARIANT_DOCK_BOTTOM_TOGGLE = mdiDockBottom
export const ICON_VARIANT_DOCK_LEFT_TOGGLE = mdiDockLeft
export const ICON_VARIANT_DOCK_RIGHT_TOGGLE = mdiDockRight
export const ICON_VARIANT_FILTER = mdiFilter
export const ICON_VARIANT_LEFT = mdiArrowLeft
// A true chevron, not `mdiMenuDown`'s small filled triangle. A split-button
// affordance needs a mark that reads at 16-18px beside a text label, and the
// triangle disappears there (AGL-1152).
export const ICON_VARIANT_CHEVRON_DOWN = mdiChevronDown
export const ICON_VARIANT_MENU_DOWN = mdiMenuDown
export const ICON_VARIANT_MODIFY_ADD = mdiPlus
export const ICON_VARIANT_MODIFY_COPY = mdiContentCopy
export const ICON_VARIANT_MODIFY_DELETE = mdiWindowClose
export const ICON_VARIANT_MODIFY_DRAG = mdiDrag
export const ICON_VARIANT_MODIFY_DUPLICATE = mdiContentDuplicate
export const ICON_VARIANT_MODIFY_EDIT = mdiPencil
export const ICON_VARIANT_MODIFY_MODE_REARRANGE = mdiCursorMove
export const ICON_VARIANT_MODIFY_MODE_SELECT = mdiCursorDefault
export const ICON_VARIANT_MODIFY_MOVE_DOWN = mdiArrowDown
// Reparenting a node in the hierarchy is an outdent / indent, and reads as
// one (AGL-1405) — the arrows above are sibling reordering.
export const ICON_VARIANT_MODIFY_MOVE_IN = mdiFormatIndentIncrease
export const ICON_VARIANT_MODIFY_MOVE_OUT = mdiFormatIndentDecrease
export const ICON_VARIANT_MODIFY_MOVE_UP = mdiArrowUp
export const ICON_VARIANT_MODIFY_PASTE = mdiContentPaste
export const ICON_VARIANT_MODIFY_REDO = mdiRedo
export const ICON_VARIANT_MODIFY_SAVE = mdiContentSave
export const ICON_VARIANT_MODIFY_UNDO = mdiUndo
export const ICON_VARIANT_NEW_TAB = mdiOpenInNew
export const ICON_VARIANT_RIGHT = mdiArrowRight
export const ICON_VARIANT_SEARCH = mdiMagnify
export const ICON_VARIANT_SELECT_PARENT = mdiArrowULeftTop
export const ICON_VARIANT_SHOW_DETAIL = mdiPageNextOutline
export const ICON_VARIANT_SHOW_MORE = mdiDotsHorizontal
export const ICON_VARIANT_SHOW_MORE_VERTICAL = mdiDotsVertical

// Areas + Pages
export const ICON_VARIANT_APP_PREFERENCES = mdiTune
export const ICON_VARIANT_APP_SETTINGS = mdiCog
export const ICON_VARIANT_BESIGNER = mdiMathCompass
export const ICON_VARIANT_HOME = mdiHomeVariant
export const ICON_VARIANT_ORGANIZATION = mdiDomain
export const ICON_VARIANT_PAGES = mdiWeb
export const ICON_VARIANT_SIGN_IN = mdiLoginVariant
export const ICON_VARIANT_SIGN_OUT = mdiLogoutVariant
export const ICON_VARIANT_USER_SETTINGS = mdiAccountSettings
export const ICON_VARIANT_HOST_GROUP = mdiWebBox
export const ICON_VARIANT_HOST = mdiWeb

// Canvas visibility (AGL-592): whether an element is shown while it is being
// designed. Distinct from whether the published site shows it — the eye
// reports and toggles the CANVAS, never the document.
export const ICON_VARIANT_VISIBILITY_SHOWN = mdiEyeOutline
export const ICON_VARIANT_VISIBILITY_HIDDEN = mdiEyeOffOutline

// Views + Panels
export const ICON_VARIANT_ELEMENT_BROWSE = mdiGroup
export const ICON_VARIANT_ELEMENT_DETAILS = mdiInformationVariant
export const ICON_VARIANT_ELEMENT_PROPERTIES = mdiFormDropdown
export const ICON_VARIANT_ELEMENT_INTERACTIONS = mdiGestureTapHold
export const ICON_VARIANT_ELEMENT_STYLES = mdiPaletteOutline
export const ICON_VARIANT_ELEMENT_TREE_VIEW = mdiFileTree

// Theme mode
export const ICON_VARIANT_THEME_DARK = mdiWeatherNight
export const ICON_VARIANT_THEME_LIGHT = mdiWhiteBalanceSunny
export const ICON_VARIANT_THEME_SYSTEM = mdiThemeLightDark

// Devices
export const ICON_VARIANT_FLUID_RESPONSIVE = mdiArrowExpandHorizontal
export const ICON_VARIANT_LAPTOP = mdiLaptop
export const ICON_VARIANT_MOBILE = mdiCellphone
export const ICON_VARIANT_MONITOR_LARGE = mdiMonitor
export const ICON_VARIANT_MONITOR_SMALL = mdiMonitorSmall
export const ICON_VARIANT_TABLET = mdiTablet

// Symbols + Status
export const ICON_VARIANT_SYMBOL_CONFIRMED = mdiCheck
// Release-flagged surface marker (AGL-229): staff-only badge on nav tabs
// and admin flag rows for features hidden from customers.
export const ICON_VARIANT_SYMBOL_FLAG = mdiFlagVariant
// Staff system-email templates (AGL-748).
export const ICON_VARIANT_SYMBOL_MAIL = mdiEmailOutline
export const ICON_VARIANT_SYMBOL_MINUS = mdiMinus
export const ICON_VARIANT_SYMBOL_SECURE = mdiShieldLock
